/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { useFacilityAlerts } from 'sarsaparilla';
import { useDocumentTitle } from 'shared-ui/utils/seo-utils';
import { useSendGAEvent } from 'shared-ui/utils/gaCommon';

import { INVENTORY_TIMED_ENTRY } from 'ui-field-sales/src/constants/types';

import { TourDetailsPage } from '../../components/TourDetailsPage';
import { useAccountType } from '../../hooks/accountHooks';
import { fetchFacilityData } from '../../actions/facilityDataActions';
import { fetchToursByFacility } from '../../actions/toursDataActions';
import { fetchTourAvailability } from '../../actions/tourAvailabilityActions';
import { setDateFilter } from '../../actions/filtersActions';
import { isAccountAllowed } from '../../utils/tourUtils';
import { tourHasExternalGuestTypes } from '../../utils';

const emptyObject = {};

export function TourDetailsPageContainer() {
    const dispatch = useDispatch();
    const alerts = useFacilityAlerts();
    const { facilityId, tourId } = useParams();
    const [searchParams] = useSearchParams();
    const { isCommAccount } = useAccountType();

    const agencyName = useSelector((state) => state.timedEntry.facility.agency_name);
    const facilityData = useSelector((state) => state.timedEntry.facility);
    const recAreaData = useSelector((state) => state.timedEntry.recArea);
    const tourData =
        useSelector((state) => state.timedEntry.tours?.tourMap?.[tourId]) || emptyObject;
    const tourListData = useSelector((state) => state.timedEntry.tours);
    const tourImagesData = useSelector((state) => state.timedEntry.tourImages);
    const tourRatingsData = useSelector((state) => state.timedEntry.tourRatings);
    const tourAvailabilityData = useSelector(
        (state) => state.timedEntry.tourAvailability
    );
    const wasGaEventFired = useRef(false);

    const dateFromSearchParams = searchParams.get('date');

    const dateFromQuery = useMemo(() => {
        if (dateFromSearchParams) {
            const localDate = moment(dateFromSearchParams);
            if (localDate?.isValid()) {
                return localDate;
            }
        }
        return null;
    }, [dateFromSearchParams]);

    const {
        isLoading: isFacilityLoading,
        facility_name: facilityName,
        facility_id: reduxFacilityId,
        guest_types: guestTypes,
    } = facilityData;

    const { isLoading: isRecAreaLoading } = recAreaData;

    const { isLoading: areToursLoading } = tourListData;

    const { isLoading: areImagesLoading, data: tourImageMap } = tourImagesData;

    const { isLoading: areRatingsLoading, data: tourRatingsMap } = tourRatingsData;

    const { tourId: reduxTourId } = tourAvailabilityData;

    useDocumentTitle(`Order Details - ${process.env.SITE_NAME}`);

    useEffect(() => {
        if (reduxFacilityId && dateFromQuery) {
            dispatch(setDateFilter(dateFromQuery));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateFromQuery, reduxFacilityId]);

    /**
     * If there is no redux data OR the redux facilityId doesn't match the URL,
     * we fetch all the data
     */
    useEffect(() => {
        if (!tourData || facilityId !== reduxFacilityId) {
            dispatch(fetchFacilityData(facilityId));
            dispatch(fetchToursByFacility(facilityId));
            dispatch(fetchTourAvailability({ facilityId, tourId, date: moment() }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityId, reduxFacilityId, tourData, tourId, reduxTourId]);

    const tourKey = `${facilityId}_${tourId}`;

    const galleryImages = tourImageMap?.[tourKey]?.galleryImages;
    const ratingSummary = tourRatingsMap?.[tourId] || emptyObject;
    const {
        tour_name: tourName,
        long_description: introTextHtml,
        notes: needToKnowItems,
    } = tourData;
    const areTourDetailsLoading =
        isFacilityLoading || isRecAreaLoading || areToursLoading || areRatingsLoading;

    useEffect(() => {
        if (
            !areTourDetailsLoading &&
            (!isAccountAllowed(isCommAccount, tourData) ||
                !tourHasExternalGuestTypes(guestTypes, tourData))
        ) {
            window.location.href = `/timed-entry/${facilityId}/`;
        }
    }, [areTourDetailsLoading, facilityId, guestTypes, isCommAccount, tourData]);

    useDocumentTitle(`${tourData.tour_name}, ${facilityName} - Recreation.gov`);

    // Fire the view_item GA Event
    useSendGAEvent({
        itemLevel: 'Inventory',
        facilityId,
        itemCategory: INVENTORY_TIMED_ENTRY,
        inventoryId: tourId,
        inventoryName: tourName,
    });

    return (
        <TourDetailsPage
            areTourDetailsLoading={areTourDetailsLoading}
            facilityName={facilityName}
            facilityId={facilityId}
            tourName={tourName}
            tourId={tourId}
            areImagesLoading={areImagesLoading}
            galleryImages={galleryImages}
            ratingSummary={ratingSummary}
            introTextHtml={introTextHtml}
            needToKnowItems={needToKnowItems}
            alerts={alerts}
        />
    );
}
